import { formatInTimeZone as dateTzFormatInTimezone, format as dateTzFormat } from "date-fns-tz";
import { TZ, getLocalTimezoneOverride } from "./timezone";
import { dayParts } from "./dayParts";
import { getLocalLanguage } from "./language";

// TODO: Probably handle format strings like "YYYY-MM-DD" and "YYYYMMDD" etc
const FormatShorthands = {
  ll: {
    year: "numeric",
    month: "short",
    day: "numeric",
  } as DateTimeFormatOptions,
  LL: { year: "numeric", month: "long", day: "numeric" },
  lll: {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  } as DateTimeFormatOptions,
  llll: {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    weekday: "short",
    minute: "2-digit",
  } as DateTimeFormatOptions,
  "ddd MMMM Do": {
    weekday: "short",
    month: "long",
    day: "numeric",
  } as DateTimeFormatOptions,
  MMM: { month: "short" } as DateTimeFormatOptions,
  LT: { hour: "numeric", minute: "2-digit" } as DateTimeFormatOptions,
  dddd: { weekday: "long" } as DateTimeFormatOptions,
  medium: { dateStyle: "medium" } as DateTimeFormatOptions,
  full: { dateStyle: "full" } as DateTimeFormatOptions,
  long: { dateStyle: "long" } as DateTimeFormatOptions,
  "MMM d": { month: "short", day: "numeric" } as DateTimeFormatOptions,
} as const;

export type DateTimeFormatOptions = ConstructorParameters<typeof Intl.DateTimeFormat>[1];

export type DateFormatOptions = (DateTimeFormatOptions & { locale?: string }) | keyof typeof FormatShorthands;

export type TranslatedString = string & { __brand: "TranslatedString" };

/** **format**: this is a simple shorthand for toLocaleString, using the current
 *  locale chosen by the user. This is almost always what you want to
 *  use. In rare cases, you may need to use formatWithPattern or
 *  formatLocalTime. */
export function format(date: Date, options?: DateFormatOptions): TranslatedString {
  if (typeof options === "undefined") {
    const tzOverride = getLocalTimezoneOverride();
    if (tzOverride) {
      return date.toLocaleString(getLocalLanguage(), { timeZone: tzOverride }) as TranslatedString;
    }
    return date.toISOString() as TranslatedString;
  }

  if (typeof options === "string") {
    if (!(options in FormatShorthands)) throw new Error(`invalid shorthand ${options}`);
  }

  const parsedOptions = typeof options === "string" ? { ...FormatShorthands[options] } : { ...options };

  if (!parsedOptions.timeZone) {
    parsedOptions.timeZone = getLocalTimezoneOverride() ?? undefined;
  }

  return date.toLocaleString(parsedOptions.locale || getLocalLanguage(), parsedOptions) as TranslatedString;
}

export function localizedWeekday(weekday: number): TranslatedString {
  if (weekday > 7) throw new Error("Numeric weekdays must be between 0 and 6");

  const formatter = new Intl.DateTimeFormat(getLocalLanguage(), {
    weekday: "long",
  });

  // 2021 starts on a Friday, so Jan 1 is Friday, so we need to offset by 2 to get to sunday (whichis what Moment returns first)
  const date = new Date(2021, 0, weekday + 3);
  return formatter.format(date) as TranslatedString;
}

export function formatCompactDate(date: Date, timeZone?: TZ): TranslatedString {
  const { year, month, day } = dayParts(date, timeZone);

  return `${year}${month}${day}` as TranslatedString;
}

export function formatISODate(date: Date, timeZone?: TZ): TranslatedString {
  const { year, month, day } = dayParts(date, timeZone);

  return `${year}-${month}-${day}` as TranslatedString;
}

/** **formatWithPattern**: You should ideally not use this, ever. It expands bundle size and is less i18n-correct than format.
 * @deprecated.
 */
export function formatWithPattern(
  date: Date,
  pattern: string,
  { timeZone }: { timeZone?: string | null } = {},
): TranslatedString {
  const tz = timeZone ?? getLocalTimezoneOverride() ?? undefined;

  return tz
    ? (dateTzFormatInTimezone(date, tz, pattern) as TranslatedString)
    : (dateTzFormat(date, pattern) as TranslatedString);
}

// Converting a date to local date string to know what the format is for that locale
export function getLocaleStringFormat(locale?: string): string {
  const localLanguage = locale ?? getLocalLanguage();
  const sample = new Date(2025, 10, 5); // October 5
  const formatter = new Intl.DateTimeFormat(localLanguage, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(sample);

  // Get the order of the parts
  const order = parts
    .filter((part) => ["year", "month", "day"].includes(part.type))
    .map((part) => {
      switch (part.type) {
        case "year":
          return "yyyy";
        case "month":
          return "MM";
        case "day":
          return "dd";
        default:
          return "";
      }
    });

  // Get the separator used
  const separators = parts
    .filter((part) => part.type === "literal")
    .map((part) => part.value)
    .filter(Boolean);

  const separator = separators[0] || "/";

  return order.join(separator);
}

function isISODateString(str: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(?:\.\d+)?(Z|([+-]\d{2}:\d{2}))?)?$/.test(str);
}

// Getting the ISO date from a locale date string
export function formatLocaleDateStringToISO(dateString: string, localeFormat?: string): string | null {
  if (isISODateString(dateString)) {
    return dateString;
  }
  const format = (localeFormat ?? getLocaleStringFormat()).toLowerCase(); // e.g., "mm/dd/yyyy"
  const parts = dateString.split(/[^0-9]/).filter(Boolean); // split on non-digits
  const formatParts = format.split(/[^a-z]/).filter(Boolean); // e.g., ["mm", "dd", "yyyy"]
  if (parts.length !== formatParts.length) {
    return null;
  }
  let day = "";
  let month = "";
  let year = "";
  for (let i = 0; i < formatParts.length; i++) {
    const part = parts[i] ?? "";
    switch (formatParts[i]) {
      case "dd":
        day = part ? part.padStart(2, "0") : "";
        break;
      case "mm":
        month = part ? part.padStart(2, "0") : "";
        break;
      case "yyyy":
        year = part;
        break;
    }
  }
  if (year && month && day) {
    return `${year}-${month}-${day}`;
  }
  return null;
}
